import type { GameState } from './stateMachine';

/**
 * Champs de l'état qui s'AFFICHENT (bandeau, score, boutons). Le reste — horloge,
 * arbitrage, calibration de l'ami — change souvent sans rien de visible : redessiner
 * recréerait les boutons, et un clic à cheval sur le rendu serait perdu.
 */
const VISIBLE = [
  'phase',
  'mode',
  'round',
  'score',
  'meReady',
  'meGrabbed',
  'myIncident',
  'rematch',
  'game',
  'throws',
  'imitation',
  'played',
] as const satisfies readonly (keyof GameState)[];

export function viewChanged(previous: GameState, next: GameState): boolean {
  return VISIBLE.some((k) => previous[k] !== next[k]);
}

/**
 * Tapette et ralenti du perdant permis dans cet état ? Barbichette : à chaque manche (et au
 * vote « visage perdu ») ; chifoumi et smiley : seulement à la fin du match.
 */
export function punishes(s: GameState): boolean {
  if (s.game === 'barbichette') return true;
  return s.phase.name === 'result' && s.phase.matchOver;
}

/**
 * Chifoumi : ma vidéo n'est pas envoyée pendant le coup (du « chi-fou » aux deux gestes
 * capturés), sinon l'ami verrait mon geste se former ; elle revient au résultat.
 */
export function hidesVideo(s: GameState): boolean {
  return s.game === 'chifoumi' && (s.phase.name === 'chant' || s.phase.name === 'throw');
}

/**
 * Vainqueur du match : le plus de manches gagnées ; à égalité (smiley), le total des scores
 * d'imitation départage ; sinon match nul.
 */
export function matchWinner(s: GameState): 'me' | 'them' | 'draw' {
  if (s.score.me !== s.score.them) return s.score.me > s.score.them ? 'me' : 'them';
  if (s.game === 'smiley' && s.points.me !== s.points.them)
    return s.points.me > s.points.them ? 'me' : 'them';
  return 'draw';
}

/** Qui reçoit la tapette et montre son ralenti dans cet état (`null` : personne). */
export function punished(s: GameState): 'me' | 'them' | null {
  const p = s.phase;
  if (p.name !== 'result' || !punishes(s)) return null;
  if (s.game === 'smiley') {
    const winner = matchWinner(s);
    return winner === 'draw' ? null : winner === 'me' ? 'them' : 'me';
  }
  return p.outcome === 'lost' ? 'me' : p.outcome === 'won' ? 'them' : null;
}
